import ApiService from '@/common/api.service'
import * as utils from '@/common/utils'
import * as findVehiclesWidgetHelpers from '@/store/assets/vehicles/findVehiclesWidget.helpers'

const module = 'admin/apiBridgeWebfleetModule'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    name: '',
    partner: {
      id: null,
      name: null
    },
    agency: {
      id: null,
      name: null
    },
    clients: [],
    created_at: '',
    updated_at: '',
    enabled: true,
    account_name: '',
    username: '',
    password: '',
    api_key: '',
    id_vehicles_by: 'reg_no',
    selection_mode: 'all',
    vehicles: [],
    // Simply keeps a list of selected vehicle ids so that we can
    // determine if any changes were made to the API bridge by the user
    selected_veh_ids: []
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

function formDefaults () {
  return {}
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param apiBridge      Object
 *
 * @return Object
 */
function transformCreate (apiBridge) {
  let vehicle_ids = []
  if (apiBridge.selection_mode === 'selected') {
    vehicle_ids = findVehiclesWidgetHelpers.selectedVehicleIds('bridge_vehicles')
  }

  return {
    id: apiBridge.id,
    name: apiBridge.name,
    enabled: apiBridge.enabled,
    client_ids: apiBridge.clients.map(client => client.id),
    account_name: apiBridge.account_name,
    username: apiBridge.username,
    password: apiBridge.password,
    api_key: apiBridge.api_key,
    id_vehicles_by: apiBridge.id_vehicles_by,
    selection_mode: apiBridge.selection_mode,
    vehicle_ids: vehicle_ids
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param apiBridge  Object
 *
 * @return Object
 */
function transformUpdate (apiBridge) {
  let vehicle_ids = []
  if (apiBridge.selection_mode === 'selected') {
    vehicle_ids = findVehiclesWidgetHelpers.selectedVehicleIds('bridge_vehicles')
  }

  return {
    id: apiBridge.id,
    name: apiBridge.name,
    enabled: apiBridge.enabled,
    client_ids: apiBridge.clients.map(client => client.id),
    account_name: apiBridge.account_name,
    username: apiBridge.username,
    password: apiBridge.password,
    api_key: apiBridge.api_key,
    id_vehicles_by: apiBridge.id_vehicles_by,
    selection_mode: apiBridge.selection_mode,
    vehicle_ids: vehicle_ids
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }

}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`api-bridges/webfleet/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`api-bridges/webfleet/create`)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`api-bridges/webfleet/${id}/edit/`)
      .then((response) => {
        const vehicles = response.data.data.vehicles
        findVehiclesWidgetHelpers.addSelectedVehicles('bridge_vehicles', vehicles)
        context.commit('set', response.data.data)
        context.commit('setProp', { name: 'selected_veh_ids', value: vehicles.map(vehicle => vehicle.id) })
        context.commit('setFormData', response.data.data)
        context.commit('setOriginal', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    const apiBridge = context.getters['get']
    return ApiService
      .post(`api-bridges/webfleet`, transformCreate(apiBridge))
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setIsModified', false)
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    const apiBridge = context.getters['get']
    return ApiService
      .put(`api-bridges/webfleet/${apiBridge.id}`, transformUpdate(apiBridge))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    const id = context.getters['get'].id
    return ApiService
      .delete(`api-bridges/webfleet/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`api-bridges/webfleet`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data.id = data.id
    state.data.name = data.name
    state.data.partner.id = data.partner.id
    state.data.partner.name = data.partner.name
    state.data.agency.id = data.agency.id
    state.data.agency.name = data.agency.name
    state.data.clients = data.clients
    state.data.account_name = data.account_name
    state.data.username = ''
    state.data.password = ''
    state.data.api_key = data.api_key
    state.data.id_vehicles_by = data.id_vehicles_by
    state.data.selection_mode = data.selection_mode
    state.data.created_at = data.created_at
    state.data.updated_at = data.updated_at
  },

  setFormData (state, data) {
    state.form = {}
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
      name: data.name,
      enabled: data.enabled,
      partner: data.partner,
      agency: data.agency,
      clients: data.clients,
      account_name: data.account_name,
      username: data.username,
      password: data.password,
      api_key: data.api_key,
      id_vehicles_by: data.id_vehicles_by,
      selection_mode: data.selection_mode,
      vehicles: data.vehicles
    }
  },

  /**
   * Add a client to the array of clients.
   *
   * @param state
   * @param client
   */
  addClient (state, client) {
    state.data.clients.push(client)
  },

  /**
   * Clear all clients from the user.
   *
   * @param state
   */
  clearClients (state) {
    state.data.clients = []
  },

  /**
   * Remove a client from the user based on the client's id.
   *
   * @param state
   * @param clientId
   */
  removeClientById (state, clientId) {
    const clientIdx = state.data.clients.findIndex(client => client.id === clientId)
    if (clientIdx > -1) {
      state.data.clients.splice(clientIdx, 1)
    }
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/administration/apiBridges/apiBridgeWebfleetModule.js